import React, { useState } from "react";
import { motion } from "framer-motion";
import { Trash2, AlertTriangle, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API } from "../api";
import { JobEventLog } from "../components/JobEventLog";
import { CARD, PageNav } from "../components/PageChrome";
import { CatalogName, DialogHeader, DIALOG_TONES, Modal, ResultOverlay } from "../components/Modal";
import { DatasourceList, DatasourcePage, useDatasources } from "../components/DatasourceList";

function ConfirmDialog({ open, target, onCancel, onConfirm, busy }) {
  const rows = [["Title", target?.title, "text-sm font-bold text-slate-900 break-words"],
                ["Endpoint", target?.endpoint_url, "text-xs font-mono text-slate-700 break-all"],
                ["Graph IRI", target?.datasource_iri, "text-xs font-mono text-slate-700 break-all"]];
  return (
    <Modal isVisible={open} z="z-[90]" className="rounded-[3rem] p-12 max-w-xl">
      <DialogHeader icon={AlertTriangle} tone={DIALOG_TONES.red} title="Confirm Deregistration"
                    caption="This action cannot be undone" />
      <div className="bg-slate-50 rounded-2xl p-6 mb-8 space-y-3">
        {rows.map(([label, value, style]) => (
          <div key={label}>
            <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest mb-1">{label}</p>
            <p className={style}>{value || "-"}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-slate-600 mb-8 leading-relaxed">
        The graph and all triples it contains will be dropped from the NEED catalog and the change synced to MongoDB.
      </p>
      <div className="flex gap-4">
        <button
          onClick={onCancel}
          disabled={busy}
          className="flex-1 py-5 bg-slate-100 text-slate-700 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all active:scale-95 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={busy}
          className="flex-1 py-5 bg-red-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-red-700 transition-all shadow-xl shadow-red-200 active:scale-95 disabled:opacity-50"
        >
          {busy ? "Deregistering..." : "Deregister"}
        </button>
      </div>
    </Modal>
  );
}

export default function Deregister({ onBack }) {
  const { logout, user } = useAuth();
  const canWrite = !!user?.can_write;
  const list = useDatasources();
  const [target, setTarget] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");

  const fail = (msg) => {
    setConfirming(false);
    setSubmitting(false);
    setFailureMessage(msg);
  };

  const startDeregister = (ds) => {
    if (!canWrite) {
      setMessage("You need an editor or administrator role to deregister a datasource.");
      return;
    }
    setMessage("");
    setTarget(ds);
    setConfirming(true);
  };

  const confirmDeregister = async () => {
    if (!target) return;
    setSubmitting(true);
    setMessage("");
    try {
      const res = await API.delete("/api/datasources", { data: { datasource_iri: target.datasource_iri } });
      if (res.status === 202 && res.data.job_id) {
        setActiveJobId(res.data.job_id);
        setConfirming(false);
      } else {
        fail("Unexpected response from server.");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      fail(typeof detail === "string" ? detail : "Deregistration failed.");
    }
  };

  const handleJobTerminal = ({ status, error }) => {
    if (status === "completed") {
      setTimeout(() => { setSubmitting(false); setIsSuccess(true); }, 600);
    } else if (status === "failed") {
      setSubmitting(false);
      setFailureMessage(error || "The backend job reported a failure while dropping the graph.");
    }
  };

  const closeSuccess = () => {
    setIsSuccess(false);
    setActiveJobId(null);
    setTarget(null);
    list.reload();
  };

  const closeFailure = () => {
    setActiveJobId(null);
    setFailureMessage("");
  };

  return (
    <div className="min-h-screen bg-slate-200 text-slate-900 font-sans">
      <PageNav accent="red" label="DEREGISTRATION" user={user} onLogout={logout} />

      <main className="pt-28 pb-20 flex justify-center px-6">
        <div className="w-full max-w-5xl">
          <div className={`${CARD} p-8 md:p-12`}>
            <DatasourcePage
              onBack={onBack}
              title="DEREGISTER A DATASOURCE"
              intro="Select a registered datasource to remove it from the NEED-Catalog."
            >
              {message && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 bg-red-50 border border-red-100 rounded-2xl text-center mb-6">
                  <span className="text-red-700 font-black text-[11px] uppercase tracking-[0.3em]">{message}</span>
                </motion.div>
              )}
              <DatasourceList
                list={list}
                user={user}
                accent="red"
                showPublishers
                renderAction={(ds) => (
                  <button
                    onClick={() => startDeregister(ds)}
                    disabled={!!activeJobId || !canWrite}
                    title={!canWrite ? "Requires an editor or administrator role" : undefined}
                    className="flex items-center justify-center gap-2 px-8 py-4 bg-red-50 text-red-700 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-red-600 hover:text-white hover:shadow-xl hover:shadow-red-100 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {canWrite ? <Trash2 size={14} /> : <Lock size={14} />}
                    Deregister
                  </button>
                )}
              />
            </DatasourcePage>

            {activeJobId && (
              <div className="mt-8 flex justify-center">
                <JobEventLog jobId={activeJobId} onTerminal={handleJobTerminal} accent="red" />
              </div>
            )}
          </div>
        </div>
      </main>

      <ConfirmDialog
        open={confirming}
        target={target}
        onCancel={() => { if (!submitting) { setConfirming(false); setTarget(null); } }}
        onConfirm={confirmDeregister}
        busy={submitting}
      />
      <ResultOverlay isVisible={isSuccess} success title="Deregistration Complete" buttonLabel="Continue" onClose={closeSuccess}>
        The datasource was removed from the <CatalogName />.
      </ResultOverlay>
      <ResultOverlay isVisible={!!failureMessage} title="Deregistration Failed" buttonLabel="Dismiss" onClose={closeFailure}>
        {failureMessage}
      </ResultOverlay>
    </div>
  );
}
